import { useCallback, useMemo, useState } from 'react'

import { FractionExpandNumQuestionModel, getFractionExpandNumQuestion as gFracQues } from './FractionExpandNum.utils'

interface AnswerModel {
  leftNumFactor?: string
  leftDenomFactor?: string
  rightDenom?: string
}

interface ErrorModel {
  leftNumFactor?: boolean
  leftDenomFactor?: boolean
  rightDenom?: boolean
}

interface ReturnType {
  question: FractionExpandNumQuestionModel
  formValid: boolean
  answer: AnswerModel
  error?: ErrorModel
  handleCheckAnswer: () => void
  onInputChange: (input?: InputTypes, val?: string) => void
  goToNextQuestion: () => void
}

interface UseProperFractionProps {
  difficultyLevel: number
  onNextQuestionClick: () => void
  onAnswerStateChange: (value: boolean | null) => void
}

type InputTypes = 'leftNumFactor' | 'leftDenomFactor' | 'rightDenom'

function useFractionExpandNum({
  difficultyLevel,
  onAnswerStateChange,
  onNextQuestionClick,
}: UseProperFractionProps): ReturnType {
  const [taskHistory, setTaskHistory] = useState<string[]>([])
  const [answer, setAnswer] = useState<AnswerModel>({})
  const [error, setError] = useState<ErrorModel>()
  const question = useMemo(() => gFracQues(difficultyLevel, taskHistory), [difficultyLevel, taskHistory])

  const handleCheckAnswer = useCallback((): void => {
    const { leftNumFactor = '0', leftDenomFactor = '0', rightDenom = '0' } = answer
    const { factor, leftDenom } = question
    const err: ErrorModel = {
      leftNumFactor: factor !== Number(leftNumFactor),
      leftDenomFactor: factor !== Number(leftDenomFactor),
      rightDenom: Number(rightDenom) !== leftDenom * factor,
    }
    onAnswerStateChange(!(err.leftNumFactor || err.leftDenomFactor || err.rightDenom))
    setError(err)
  }, [question, answer, onAnswerStateChange, setError])

  const onInputChange = useCallback(
    (input?: InputTypes, val?: string) => {
      switch (input) {
        case 'leftNumFactor':
          setAnswer({ ...answer, leftNumFactor: val })
          break
        case 'leftDenomFactor':
          setAnswer({ ...answer, leftDenomFactor: val })
          break
        case 'rightDenom':
          setAnswer({ ...answer, rightDenom: val })
          break
        default:
          setAnswer({})
      }
    },
    [answer],
  )

  const goToNextQuestion = useCallback(() => {
    const { leftDenom, leftNum, factor } = question
    const newTaskHistory = [`${factor}-${leftNum}-${leftDenom}`, ...taskHistory].slice(0, question.maxVariation)
    setTaskHistory(newTaskHistory)
    onInputChange()
    onNextQuestionClick()
    setError(undefined)
  }, [taskHistory, onInputChange, onNextQuestionClick, question])

  return {
    answer,
    formValid: !!answer.leftNumFactor && !!answer.leftDenomFactor && !!answer.rightDenom,
    question,
    error,
    handleCheckAnswer,
    onInputChange,
    goToNextQuestion,
  } as const
}

export default useFractionExpandNum
